import React from 'react';
import { Box, TextButton, Button } from '@wix/design-system';
import type { Subcategory } from '@wix/app-market-components';

interface SubCategoriesProps {
  subCategories: Subcategory[];
  onTagClick(
    categorySlug?: string,
    subCategory?: string,
    label?: 'category' | 'sub_category',
    location?: 'top_page' | 'side_category_page',
  ): void;
  DesignComponent: typeof TextButton | typeof Button;
}

export function CategoriesTags({
  subCategories,
  onTagClick,
  DesignComponent,
}: SubCategoriesProps) {
  function organizeSubCategories(subCategoriesList: Subcategory[]) {
    const organizedCategories: {
      parentName: string;
      parentSlug: string;
      subCategories: { name: string; slug: string }[];
    }[] = [];

    subCategoriesList.forEach((subcategory) => {
      const parentIndex = organizedCategories.findIndex(
        (category) => category.parentName === subcategory.parentName,
      );

      if (parentIndex !== -1) {
        organizedCategories[parentIndex].subCategories.push({
          name: subcategory.name,
          slug: subcategory.slug,
        });
      } else {
        organizedCategories.push({
          parentName: subcategory.parentName,
          parentSlug: subcategory.parentSlug,
          subCategories: [{ name: subcategory.name, slug: subcategory.slug }],
        });
      }
    });

    return organizedCategories;
  }

  const tags = organizeSubCategories(subCategories);
  const theme = DesignComponent === TextButton ? 'lightOutlined' : undefined;
  const size = DesignComponent === Button ? 'tiny' : 'small';
  const underline = DesignComponent === TextButton ? 'always' : undefined;
  const priority = DesignComponent === Button ? 'secondary' : undefined;
  const style =
    DesignComponent === Button
      ? { outline: '1px solid var(--Foundation-Blue-B10, #116DFF)' }
      : {};

  return (
    <Box dataHook="categories-tags" gap="18px">
      {tags.map((category) => (
        <Box
          columnGap={DesignComponent === TextButton ? '18px' : '6px'}
          rowGap={DesignComponent === TextButton ? '6px' : '8px'}
          flexWrap="wrap"
        >
          <DesignComponent
            onClick={() =>
              onTagClick(
                category.parentSlug,
                '',
                'category',
                DesignComponent === Button ? 'side_category_page' : 'top_page',
              )
            }
            key={category.parentName}
            theme={theme}
            removable={false}
            size={size}
            weight="normal"
            underline={underline}
            priority={priority}
            style={style}
          >
            {`${category.parentName
              .charAt(0)
              .toUpperCase()}${category.parentName.slice(1)}`}
          </DesignComponent>

          {category.subCategories.map((subCategory, subIndex) => (
            <DesignComponent
              onClick={() =>
                onTagClick(
                  category.parentSlug,
                  subCategory.slug,
                  'sub_category',
                  DesignComponent === Button
                    ? 'side_category_page'
                    : 'top_page',
                )
              }
              dataHook={`tag-${category.parentName}-${subCategory.slug}`}
              key={`${category.parentName}-${subCategory.slug}-${subIndex}`}
              theme={theme}
              size={size}
              weight="normal"
              underline={underline}
              priority={priority}
              style={style}
            >
              {`${subCategory.name
                .charAt(0)
                .toUpperCase()}${subCategory.name.slice(1)}`}
            </DesignComponent>
          ))}
        </Box>
      ))}
    </Box>
  );
}
